import { META_AD_UTM } from 'src/@types/leadform/leadform.contract';
import type { ShortLinkItem } from 'src/@types/leadform/leadform.contract';
import {
  absoluteUrl,
  adLink as logicAdLink,
  applyPath,
  generalPath,
  generalThanksPath,
  shortLinkPath,
  shortTagQuery,
  thanksPath,
} from 'src/@types/leadform/leadform.logic';

// Public site links (the diploma landing pages). Same resolution order as the
// API base URL in serviceUrls.ts: the runtime key written by
// docker/40-runtime-env.sh, then the build-time env, then production.
export const siteBase = (): string =>
  (
    (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__?.REACT_APP_SITE_URL) ||
    process.env.REACT_APP_SITE_URL ||
    'https://kayfa.io'
  ).replace(/\/+$/, '');

/** True when the links lead to the production site (https://kayfa.io). */
export const isProductionSite = (): boolean => /^https:\/\/(www\.)?kayfa\.io$/i.test(siteBase());

/** `/bootcamps/<slug>/` (Arabic, the default locale) or `/en/bootcamps/<slug>/`. */
export function siteUrl(slug: string, locale: 'ar' | 'en' = 'ar'): string {
  const path = `/bootcamps/${encodeURIComponent(slug || '')}/`;
  return `${siteBase()}${locale === 'en' ? '/en' : ''}${path}`;
}

/** The phone-sized preview window (a second click reuses it). */
export function openPhonePreview(slug: string): void {
  window.open(siteUrl(slug, 'ar'), 'kayfa-phone-preview', 'width=390,height=844');
}

// ---- Lead forms (lead-forms SPEC A4, D.5) -----------------------------------
// Thin wrappers over the shared leadform.logic URL builders, so the admin's
// links are byte-identical to the ones the site and the API build. Arabic is
// unprefixed, English adds /en, always a trailing slash, never /ar.

type FormLocale = 'ar' | 'en';

/** `/apply/<slug>/` (key null: the primary form) or `/apply/<slug>/<key>/`, on siteBase(). */
export function applyUrl(slug: string, key: string | null, locale: FormLocale = 'ar'): string {
  return absoluteUrl(siteBase(), locale, applyPath(slug, key));
}

/** The diploma's one thank-you URL, shared by every form and variant. */
export function thanksUrl(slug: string, locale: FormLocale = 'ar'): string {
  return absoluteUrl(siteBase(), locale, thanksPath(slug));
}

/** The full Meta ad link: the form URL + '?' + the UTM template ({{…}} macros unencoded). */
export function adLink(slug: string, key: string | null, locale: FormLocale = 'ar'): string {
  return logicAdLink(siteBase(), locale, applyPath(slug, key));
}

// ---- General forms (SPEC O.1.4, O.4.3): /form/<slug>/, never under /apply/ ----

/** `/form/<slug>/` (Arabic) or `/en/form/<slug>/`, on siteBase(). */
export function generalFormUrl(slug: string, locale: FormLocale = 'ar'): string {
  return absoluteUrl(siteBase(), locale, generalPath(slug));
}

/** The general form's own thank-you URL: `/form/<slug>/thanks/` (+ /en). */
export function generalThanksUrl(slug: string, locale: FormLocale = 'ar'): string {
  return absoluteUrl(siteBase(), locale, generalThanksPath(slug));
}

/** The Meta ad link of a general form: its URL + '?' + the UTM template. */
export function generalAdLink(slug: string, locale: FormLocale = 'ar'): string {
  return logicAdLink(siteBase(), locale, generalPath(slug));
}

/** The "URL parameters" box of a Meta ad (the same template the ad link carries). */
export const urlParams = (): string => META_AD_UTM;

// ---- Short links (SPEC-Q.4.1): /go/<code>/ is always unprefixed (the target fixes the locale) ----

/** `<site>/go/<code>/`. */
export function shortUrl(code: string): string {
  return absoluteUrl(siteBase(), 'ar', shortLinkPath(code));
}

/** The full link a short link opens: its target on siteBase() plus its stored tags. */
export function shortTargetUrl(
  item: Pick<ShortLinkItem, 'locale' | 'targetPath' | 'tags'>
): string {
  const q = shortTagQuery(item.tags);
  return absoluteUrl(siteBase(), item.locale, item.targetPath) + (q ? `?${q}` : '');
}

export type PreviewDevice = 'phone' | 'desktop';

const PREVIEW_FEATURES: Record<PreviewDevice, string> = {
  phone: 'width=390,height=844',
  desktop: 'width=1280,height=800',
};

/** `<site>[/en]/apply-preview/<token>/`: the draft preview page (a signed 1-hour token). */
export function formPreviewUrl(token: string, locale: FormLocale = 'ar'): string {
  return absoluteUrl(siteBase(), locale, `/apply-preview/${encodeURIComponent(token)}/`);
}

/**
 * Opens (or reuses) the named preview window. Call it synchronously inside the
 * click handler, before any await, or the browser blocks the popup: pass no
 * token to open a blank window, then call it again with the token (and the
 * window it returned) once the draft is saved. Returns null when blocked.
 */
export function openFormPreview(
  token: string | null,
  locale: FormLocale = 'ar',
  device: PreviewDevice = 'phone',
  win?: Window | null
): Window | null {
  const name = `kayfa-form-preview-${device}`;
  if (!token) return window.open('', name, PREVIEW_FEATURES[device]);
  const url = formPreviewUrl(token, locale);
  if (win && !win.closed) {
    win.location.href = url;
    win.focus();
    return win;
  }
  return window.open(url, name, PREVIEW_FEATURES[device]);
}
